import { FileDropzone, Flex } from "@hackthenorth/north";
import { Button } from "north.js";
import React, {
  useState,
  useCallback,
  useMemo,
  forwardRef,
  ReactNode,
} from "react";
import { styled } from "twin.macro";

import {
  AcceptedFileType,
  ALL_FILE_TYPES,
  DropzoneError,
} from "src/shared/components";
import { UploadIcon } from "src/static/icons";

export interface DropzoneProps {
  children: ReactNode;
  min?: number;
  max?: number;
  readOnly?: boolean;
  // todo: fix type later
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  label?: any; // any react element
  error?: boolean;
  acceptedFileTypes?: AcceptedFileType[];
  onFilesUpload: (files: File[]) => void;
  onError: (error: DropzoneError) => void;
  onBlur: () => void;
}

const FileDropzoneContent = styled(Flex)<{
  dragging: boolean;
  readOnly: boolean;
  error: boolean;
}>`
  position: relative;
  cursor: pointer;
  background-color: ${({ dragging, theme }) =>
    dragging
      ? theme.globalConstants.color.borderFocusLight
      : theme.globalConstants.color.white};
  border-radius: 8px;
  overflow: hidden;

  .content {
    transition: filter 0.2s;
  }

  &:hover .content {
    filter: brightness(0.8);
  }

  /* The button is hidden by default */
  .upload-button {
    opacity: 0;
    transition: opacity 0.2s, transform 0.2s;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
  }

  &:hover .upload-button {
    opacity: 1;
  }
`;

/* Prevents capturing from triggering an additional ClickEvent on the Dropzone*/
const CapturingPreventionWrapper = styled.div.attrs({
  onClick: (e: any) => e.preventDefault(),
})``;

const Dropzone = forwardRef(
  (
    {
      children,
      max,
      min,
      readOnly = false,
      error = false,
      onError,
      onBlur,
      onFilesUpload,
      acceptedFileTypes = ALL_FILE_TYPES,
    }: DropzoneProps,
    ref
  ) => {
    const [dragging, setDragging] = useState(false);

    const accept = useMemo(() => {
      return acceptedFileTypes.map((fileType) => `.${fileType}`).join(",");
    }, [acceptedFileTypes]);

    /* See @https://stackoverflow.com/a/17355937 for file validation regex*/
    const isFileValid = useCallback(
      (file: File) => {
        // special handling for pptx files
        if (
          acceptedFileTypes.includes("pptx") &&
          file.type.includes("presentation")
        ) {
          return true;
        }
        const regex = new RegExp(
          "(" + acceptedFileTypes.join("|").replace(/\./g, "\\.") + ")",
          "i"
        );
        return regex.test(file.type);
      },
      [acceptedFileTypes]
    );

    const onDragChange = useCallback((dragging: boolean) => {
      setDragging(dragging);
    }, []);

    const onDrop = useCallback(
      (files: File[]) => {
        if (min && files.length < min) {
          onError(DropzoneError.MIN_AMOUNT);
          return;
        }

        if (max && files.length > max) {
          onError(DropzoneError.MAX_AMOUNT);
          return;
        }

        if (files.find((file) => !isFileValid(file))) {
          onError(DropzoneError.INVALID_FORMAT);
          return;
        }
        onFilesUpload(files);
      },
      [min, max, isFileValid, onError, onFilesUpload]
    );

    return (
      <CapturingPreventionWrapper>
        <FileDropzone
          className="file-dropzone"
          disabled={false}
          onDragChange={onDragChange}
          options={{
            onDrop,
            noKeyboard: true,
            multiple: min !== 1 || max !== 1,
            accept,
            disabled: readOnly,
            onDropRejected: () => console.debug("rejected"),
          }}
          ref={ref as React.RefObject<HTMLInputElement>}
          readOnly={readOnly}
        >
          <FileDropzoneContent
            column
            justify="center"
            align="center"
            className="file-dropzone-content"
            tw="cursor-pointer"
            dragging={dragging}
            readOnly={readOnly}
            error={error}
            onBlur={onBlur}
          >
            <Button
              leadingIcon={<UploadIcon width={16} />}
              className="upload-button"
            >
              Upload
            </Button>
            <div className="content">{children}</div>
          </FileDropzoneContent>
        </FileDropzone>
      </CapturingPreventionWrapper>
    );
  }
);

export default Dropzone;
